import { useState, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useColor4 } from '../hooks/useColor4';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { Lightbox } from './Lightbox';

const featureImage1Def: SlotGroupDefinition = {
  label: 'Feature Image 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'The single most striking, high-quality photo of this property — the one that gives the strongest first impression of the community. Prefer professionally staged shots when relevant. Must have a clear subject, good lighting, and sharp focus; skip blurry, dark, cluttered, or redundant shots and weak subjects (toilets, empty closets, bare or dirty patios, utility areas). Alt text describes what the photo actually shows.',
    context:
      'Feature image 1 of 4 in a 4-up row of 4:3 card thumbnails under the community overview on the Home page. Lead image of the set — often an exterior or the signature view. Must be a different photo, and ideally a different category (exterior / outdoor amenity / indoor amenity / apartment interior), than feature images 2–4.',
  },
};

const featureImage2Def: SlotGroupDefinition = {
  label: 'Feature Image 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Another standout, high-quality photo of this property that shows a different side of the community than the other feature images. Prefer professionally staged shots when relevant. Must have a clear subject, good lighting, and sharp focus; skip blurry, dark, cluttered, or redundant shots and weak subjects (toilets, empty closets, bare or dirty patios, utility areas). Alt text describes what the photo actually shows.',
    context:
      'Feature image 2 of 4 in a 4-up row of 4:3 card thumbnails under the community overview on the Home page. Favor an outdoor amenity or common outdoor space if a strong one exists. Do not reuse the photo from feature images 1, 3, or 4, and avoid the same category as them when other strong options exist. Repeat a photo only if the property has too few usable images.',
  },
};

const featureImage3Def: SlotGroupDefinition = {
  label: 'Feature Image 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[6].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[6].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 6,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A standout, high-quality photo of a shared indoor space at this property, distinct from the other feature images. Prefer professionally staged shots when relevant. Must have a clear subject, good lighting, and sharp focus; skip blurry, dark, cluttered, or redundant shots and weak subjects (toilets, empty closets, bare or dirty patios, utility areas). Alt text describes what the photo actually shows.',
    context:
      'Feature image 3 of 4 in a 4-up row of 4:3 card thumbnails under the community overview on the Home page. Favor an indoor amenity or common area; if none is strong, use the best remaining photo from another category. Do not reuse the photo from feature images 1, 2, or 4. Repeat a photo only if the property has too few usable images.',
  },
};

const featureImage4Def: SlotGroupDefinition = {
  label: 'Feature Image 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[9].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[9].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 9,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A standout, high-quality photo of an apartment interior at this property, distinct from the other feature images. Prefer professionally staged living, kitchen, or bedroom shots when available. Must have a clear subject, good lighting, and sharp focus; skip blurry, dark, cluttered, or redundant shots and weak subjects (toilets, empty closets, bare or dirty patios, utility areas). Alt text describes what the photo actually shows.',
    context:
      'Feature image 4 of 4 in a 4-up row of 4:3 card thumbnails under the community overview on the Home page. Favor an apartment interior; if none is strong, use the best remaining photo from another category. Do not reuse the photo from feature images 1, 2, or 3. Repeat a photo only if the property has too few usable images.',
  },
};

export function PropertyOverview() {
  const preTitle = useContentToken('propertyoverview.pre_title');
  const title = useContentToken('propertyoverview.title');
  const image1 = useSlotGroup('propertyoverview.feature_image_1', featureImage1Def);
  const image2 = useSlotGroup('propertyoverview.feature_image_2', featureImage2Def);
  const image3 = useSlotGroup('propertyoverview.feature_image_3', featureImage3Def);
  const image4 = useSlotGroup('propertyoverview.feature_image_4', featureImage4Def);

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const lightboxImages = useMemo(
    () => [
      { url: image1.image || '', alt: image1.alt || '' },
      { url: image2.image || '', alt: image2.alt || '' },
      { url: image3.image || '', alt: image3.alt || '' },
      { url: image4.image || '', alt: image4.alt || '' },
    ],
    [image1.image, image1.alt, image2.image, image2.alt, image3.image, image3.alt, image4.image, image4.alt],
  );

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>();
  const color4 = useColor4();

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div ref={headingRef} className="reveal-fade-up">
          <EditableToken
            path="propertyoverview.pre_title"
            defaultValue="Life at our community is like nowhere else"
            as="p"
            className="font-body text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[var(--vibe-color-primary)] mb-3"
          >
            {preTitle}
          </EditableToken>

          <EditableToken
            path="propertyoverview.title"
            defaultField="property_name"
            defaultValue="Our Community"
            as="h2"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal text-[var(--vibe-color-text)] leading-tight tracking-tight"
          >
            {title}
          </EditableToken>

          <div className="w-16 h-1 mx-auto mt-5 mb-8 rounded-full bg-[var(--vibe-color-primary)]" style={color4?.primaryOverride} />

          <RichTextContent
            path="propertyoverview.description"
            as="div"
            className="font-body text-sm sm:text-base text-[var(--vibe-color-text)] max-w-3xl mx-auto mb-14 leading-relaxed [&_a]:text-primary [&_a]:underline"
          />
        </div>

        <div ref={gridRef} className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <EditableSlotGroup groupPath="propertyoverview.feature_image_1" definition={featureImage1Def}>
            <button
              data-reveal
              className="reveal-scale aspect-[4/3] overflow-hidden group cursor-zoom-in w-full relative"
              onClick={() => setLightboxIndex(0)}
              aria-label="Enlarge image 1"
            >
              <VibesitesImage
                url={image1.image || ''}
                alt={image1.alt || ''}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(min-width: 768px) 240px, 50vw"
              />
              <span className="overview-zoom-icon">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Zm-6-3v6m-3-3h6" />
                </svg>
              </span>
            </button>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="propertyoverview.feature_image_2" definition={featureImage2Def}>
            <button
              data-reveal
              className="reveal-scale aspect-[4/3] overflow-hidden group cursor-zoom-in w-full relative"
              onClick={() => setLightboxIndex(1)}
              aria-label="Enlarge image 2"
            >
              <VibesitesImage
                url={image2.image || ''}
                alt={image2.alt || ''}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(min-width: 768px) 240px, 50vw"
              />
              <span className="overview-zoom-icon">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Zm-6-3v6m-3-3h6" />
                </svg>
              </span>
            </button>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="propertyoverview.feature_image_3" definition={featureImage3Def}>
            <button
              data-reveal
              className="reveal-scale aspect-[4/3] overflow-hidden group cursor-zoom-in w-full relative"
              onClick={() => setLightboxIndex(2)}
              aria-label="Enlarge image 3"
            >
              <VibesitesImage
                url={image3.image || ''}
                alt={image3.alt || ''}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(min-width: 768px) 240px, 50vw"
              />
              <span className="overview-zoom-icon">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Zm-6-3v6m-3-3h6" />
                </svg>
              </span>
            </button>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="propertyoverview.feature_image_4" definition={featureImage4Def}>
            <button
              data-reveal
              className="reveal-scale aspect-[4/3] overflow-hidden group cursor-zoom-in w-full relative"
              onClick={() => setLightboxIndex(3)}
              aria-label="Enlarge image 4"
            >
              <VibesitesImage
                url={image4.image || ''}
                alt={image4.alt || ''}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(min-width: 768px) 240px, 50vw"
              />
              <span className="overview-zoom-icon">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Zm-6-3v6m-3-3h6" />
                </svg>
              </span>
            </button>
          </EditableSlotGroup>
        </div>
      </div>

      <Lightbox
        images={lightboxImages}
        selectedIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onSelect={setLightboxIndex}
      />

      <style>{`
        .overview-zoom-icon {
          position: absolute;
          bottom: 0.5rem;
          right: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          border-radius: 9999px;
          background: rgba(0, 0, 0, 0.45);
          color: #fff;
          opacity: 0;
          transition: opacity 0.2s ease;
          pointer-events: none;
        }
        button:hover .overview-zoom-icon,
        button:focus-visible .overview-zoom-icon {
          opacity: 1;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.pre_title': {
      label: 'Subtitle',
      type: 'text',
      defaultValue: 'Life at our community is like nowhere else',
    },
    'propertyoverview.title': {
      label: 'Title',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Our Community',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue: '<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>',
    },
  },
  slotGroups: {
    'propertyoverview.feature_image_1': featureImage1Def,
    'propertyoverview.feature_image_2': featureImage2Def,
    'propertyoverview.feature_image_3': featureImage3Def,
    'propertyoverview.feature_image_4': featureImage4Def,
  },
};

(PropertyOverview as any).contentSchema = contentSchema;

export default PropertyOverview;
